import { Box, Button, FormControl, IconButton, InputLabel, List, ListItem, ListItemIcon, ListItemText, MenuItem, Select, Stack, Tooltip, Typography } from "@mui/material";
import { FaMoneyBill, FaPen, FaTrash } from "react-icons/fa6";
import { getPaymentMethods } from "../../../helpers/http_payment_methods_requests";
import { useQuery } from "@tanstack/react-query";
import { centsToReais, formatCurrency, reaisToCents } from "../../../utils/functions";
import { useModal } from "../../../providers/ModalProvider";
import { useEffect, useState, useMemo } from "react";
import globalToast from "../../../components/common/globalToast";

export default function PaymentMethodManagement({ handleRemovePayment, handleResetPayment, paymentList, handleIncludePayment, isPaymentAvailable, hasProducts, isCustomerDefined, currentTotalPrice = '0', handleCreateOrder, productList }) {

    const { setModalTitle, handleOpen, setFormFieldList, setInitialData, setOnSubmitHandler } = useModal();

    const [fieldValue, setFieldValue] = useState('');

    const [restValue, setRestValue] = useState(0);

    const {
        data: paymentMethodsData,
        isFetching,
    } = useQuery({
        queryKey: ['getPaymentMethods'],
        queryFn: getPaymentMethods,
        staleTime: 1000 * 15,
    });

    const { data: paymentMethods = [] } = paymentMethodsData?.data ?? {};

    const formFieldList = [
        {
            title: "Detalhes da Forma de Pagamento",
            fields: [
                {
                    name: "price",
                    label: "Preço",
                    type: "text",
                    mask: "currency",
                    required: true
                },
                {
                    name: "description",
                    label: "Descrição",
                    type: "text",
                    multiline: true,
                    rows: 5,
                    fullWidth: true,
                    required: true,
                },
            ]
        },
    ];

    const handleSavePayment = (data) => {
        handleIncludePayment({ ...data, price: reaisToCents(data.price) });
    };

    const handleUpdatePayment = (data) => {
        const price = reaisToCents(data.price);

        if (price > restValue) {
            globalToast.error('Por favor, insira um valor válido.')

        } else {

            handleRemovePayment(data.index);
            handleIncludePayment({ ...data, price });

        };

    };

    const handleOpenModal = (e) => {

        const currentPayment = paymentMethods.find(el => el.id == e.target.value);

        setModalTitle(currentPayment.name);
        setFormFieldList(formFieldList);
        setInitialData({ ...currentPayment, name: currentPayment.name, price: centsToReais(restValue) });
        setOnSubmitHandler(() => handleSavePayment);
        handleOpen();
        setFieldValue('');
    };

    const handleOpenUpdateModal = (data) => {
        setModalTitle(data.name);
        setFormFieldList(formFieldList);
        setInitialData({ ...data, price: centsToReais(data.price) });
        setOnSubmitHandler(() => () => handleUpdatePayment(data));
        handleOpen();
    };

    const paymentSum = useMemo(() => {
        return paymentList.reduce((sum, obj) => sum + Number(obj.price), 0);
    }, [paymentList]);

    useEffect(() => {
        if (!hasProducts) {
            handleResetPayment();
            return setRestValue(0);
        };
        setRestValue(Number(currentTotalPrice) - paymentSum);
    }, [hasProducts, paymentSum, currentTotalPrice, handleResetPayment]);

    return (
        <Box sx={styles.box}>
            <Box sx={styles.header}>
                <Box sx={styles.headerContainer}>
                    <Box component={FaMoneyBill} fontSize={'1.25rem'} />
                    <Typography sx={styles.headerText}>Forma de Pagamento</Typography>
                </Box>
                <Tooltip title={"Adicione produtos no carrinho para continuar"} arrow>
                    <FormControl size="small" sx={styles.formControl} fullWidth>
                        <InputLabel id="demo-simple-select-label">Forma de pagamento</InputLabel>
                        <Select
                            disabled={isFetching || !isPaymentAvailable || restValue === 0}
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            label="Forma de pagamento"
                            value={fieldValue}
                            onChange={handleOpenModal}
                        >
                            {paymentMethods?.map((obj) => (
                                <MenuItem key={obj.id} value={obj.id}>{obj.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Tooltip>
            </Box>
            <List sx={styles.list}>
                {paymentList?.map((data, index) => (
                    <ListItem key={index} sx={styles.listItem}>
                        <ListItemIcon>
                            <Box component={FaMoneyBill} fontSize={24} />
                        </ListItemIcon>
                        <ListItemText>
                            <Box sx={styles.listTextContainer}>
                                <Typography>{data.name}</Typography>
                                <Stack direction={'row'} spacing={1}>
                                    <Typography fontWeight={600}>{formatCurrency(data.price)}</Typography>
                                    <IconButton size="small" onClick={() => handleOpenUpdateModal({ ...data, index: index })}>
                                        <Box component={FaPen} color={'black'} />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => handleRemovePayment(index)}>
                                        <Box component={FaTrash} color={'red'} />
                                    </IconButton>
                                </Stack>
                            </Box>
                        </ListItemText>
                    </ListItem>
                ))}
            </List>
            <Box sx={styles.totalContainer}>
                <Box sx={styles.remainingContainer}>
                    <Typography>Restante</Typography>
                    <Typography>{formatCurrency(restValue)}</Typography>
                </Box>
                <Box sx={styles.totalValueContainer}>
                    <Typography fontSize={'1.25rem'}>Total</Typography>
                    <Typography fontSize={'1.25rem'}>{formatCurrency(currentTotalPrice)}</Typography>
                </Box>
                <Tooltip title={!hasProducts && "Adicione produtos no carrinho para continuar"} arrow>
                    <span style={{ width: '100%' }}>
                        <Button fullWidth disabled={!isCustomerDefined || !hasProducts || restValue !== 0} variant="contained" sx={styles.button} onClick={handleCreateOrder} size="large">
                            Finalizar pedido
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

const styles = {
    box: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'start',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: 2,
        boxSizing: "border-box",
    },
    header: {
        display: 'flex',
        justifyContent: 'start',
        flexDirection: 'column',
        width: '100%',
        height: '25%',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        width: '100%'
    },
    headerText: {
        ml: '.5rem',
        fontSize: '1.25rem',
        textAlign: 'left',
    },
    formControl: {
        my: '1rem',
    },
    list: {
        overflow: 'auto',
        height: '15vh',
        maxHeight: '15vh',
        width: '100%',
    },
    listItem: {
        width: '100%'
    },
    listTextContainer: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    totalContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '40%',
        flexDirection: 'column'
    },
    remainingContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    totalValueContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: '1rem',
    },
    button: {
        py: '1rem',
    },
};
